import { Link, useNavigate, useParams } from 'react-router';
import { startRunBlock } from '@ecobox/domain';
import {
  CommandValidationError,
  formatAddress,
  needsLoadScan,
  runProgress,
  stopBarcodes,
  type OpsPickupRequest,
  type OpsShipment,
  type StopView,
} from '@ecobox/firebase';
import { Badge, Button, Card, EmptyState, TimeInState, useToast } from '@ecobox/ui';
import { useDriverData } from '../../hooks/useDriver';
import { useRunBranch } from '../../hooks/useRunBranch';
import { RUN_TYPE_LABELS } from '@ecobox/firebase';
import { useServices, useSession } from '../../services';
import { useAllSessions, usePermissions, useSessionEngine } from '../../sessions';
import {
  BackLink,
  ContactLinks,
  Problems,
  Progress,
  Tile,
  fmt,
  runStatus,
  stopStatus,
} from './shared';

export function RunPage() {
  const { runId = '' } = useParams();
  const data = useDriverData();
  const run = data.runs.find((r) => r.id === runId);
  const stops = data.stopsOf(runId);
  const navigate = useNavigate();
  const toast = useToast();
  const { writer } = useServices();
  const { uid } = useSession();
  const engine = useSessionEngine();
  const sessions = useAllSessions();
  const perms = usePermissions();
  const { wrongBranch } = useRunBranch(run?.branchId);

  if (!run) {
    return (
      <EmptyState
        title={data.loading ? 'Loading your run…' : 'This run is not on your phone'}
        description={
          data.loading
            ? 'It opens as soon as it has loaded.'
            : 'It may be finished, or it is assigned to someone else. Check Today.'
        }
        action={
          <Link to="/today">
            <Button size="touch">Back to Today</Button>
          </Link>
        }
      />
    );
  }

  const list = stops ?? [];
  const progress = runProgress(run, stops);
  const status = runStatus(run.status);
  const canRun = perms.has('finalmile.deliver') || perms.has('pickup.complete');
  // A run with deliveries needs a driver who delivers; a run of pickups takes either (04 §4.5).
  const startBlock = startRunBlock((p) => perms.has(p), list);
  const canStart = startBlock === null;
  const loadScan = needsLoadScan(list);
  const loading = sessions.find(
    (s) =>
      s.kind === 'run_load' && s.targetId === run.id && s.uid === uid && s.finishedAt === undefined,
  );
  const firstOpen = list.find((s) => s.status === 'pending' || s.status === 'arrived');
  const problems = data.problemsOf(run.id);

  const startLoad = () => {
    if (!uid) return;
    engine.start({
      kind: 'run_load',
      targetId: run.id,
      targetLabel: `${RUN_TYPE_LABELS[run.type]} · ${run.date}`,
      uid,
      branchId: run.branchId,
    });
    void navigate(`/run/${run.id}/load`);
  };

  const startNow = () => {
    try {
      writer.send('startRun', { runId: run.id, barcodes: [] });
      toast.show({
        title: 'Run started',
        message: 'Saved. It goes to the server when you have signal.',
        tone: 'success',
      });
    } catch (err) {
      toast.show({
        title: 'Not started',
        message:
          err instanceof CommandValidationError && err.issues.length > 0
            ? err.issues.join('; ')
            : err instanceof Error
              ? err.message
              : String(err),
        tone: 'danger',
      });
    }
  };

  return (
    <div className="ops-stack">
      <BackLink to="/today">Today</BackLink>
      <div>
        <h1 className="ops-title">{RUN_TYPE_LABELS[run.type]}</h1>
        <p className="ops-muted" style={{ margin: 0 }}>
          {run.date}
          {run.vehiclePlate ? ` · ${run.vehiclePlate}` : ''} · {run.branchId}{' '}
          <Badge tone={run.waiting ? 'warning' : status.tone}>
            {run.waiting ? `${status.label} · waiting to sync` : status.label}
          </Badge>
        </p>
      </div>

      {wrongBranch ? (
        <p role="alert" className="ops-error">
          This run belongs to {run.branchId}, but your phone is set to another branch. Change the
          branch in Me before you send anything.
        </p>
      ) : null}
      {!canRun ? (
        <p role="alert" className="ops-error">
          Running deliveries and pickups is not part of your role.
        </p>
      ) : startBlock === 'delivery_permission' &&
        (run.status === 'planned' || run.status === 'assigned') ? (
        <p role="alert" className="ops-error">
          This run has deliveries, and delivering is not part of your role, so you cannot start it.
          Ask your dispatcher to give it to a driver who delivers.
        </p>
      ) : null}

      <div className="ops-stack" data-tour="run-progress">
        <Progress percent={progress.percent} label="Stops finished" />
        <div className="ops-tiles">
          <Tile n={progress.left} label="stops left" tone={progress.left > 0 ? 'wait' : 'ok'} />
          <Tile n={progress.done} label="done" tone="ok" />
          {progress.failed > 0 ? <Tile n={progress.failed} label="failed" tone="bad" /> : null}
          <Tile n={fmt(run.cashCollectedMinor, run.currency)} label="cash collected" />
        </div>
      </div>

      <Problems problems={problems} />

      {run.status === 'planned' || run.status === 'assigned' ? (
        <Card title="Start the run" className="ops-card--next" data-tour="run-start">
          <div className="ops-stack">
            {loadScan ? (
              <>
                <p style={{ margin: 0 }}>
                  Scan every box as it goes on the van. Planned boxes you do not scan stay at the
                  branch.
                </p>
                <Button size="touch" variant="cta" block disabled={!canStart} onClick={startLoad}>
                  {loading ? 'Continue the load scan' : 'Scan the load and start'}
                </Button>
              </>
            ) : (
              <>
                <p style={{ margin: 0 }}>
                  No boxes to load for this run. Start it when you leave the branch.
                </p>
                <Button size="touch" variant="cta" block disabled={!canStart} onClick={startNow}>
                  Start the run
                </Button>
              </>
            )}
          </div>
        </Card>
      ) : null}

      <section className="ops-stack" aria-label="Stops" data-tour="run-stops">
        <h2 className="ops-subtitle">Stops</h2>
        {stops === undefined ? (
          <p className="ops-muted" style={{ margin: 0 }}>
            {data.loading ? 'Loading the stops…' : 'The stops are not on this phone yet.'}
          </p>
        ) : list.length === 0 ? (
          <p className="ops-muted" style={{ margin: 0 }}>
            This run has no stops.
          </p>
        ) : (
          <ul className="ops-list" aria-label="Stop list">
            {list.map((s) => (
              <StopRow
                key={s.id}
                stop={s}
                runId={run.id}
                currency={run.currency}
                isNext={firstOpen?.id === s.id}
                shipment={s.shipmentId ? data.shipments.get(s.shipmentId) : undefined}
                pickup={s.pickupRequestId ? data.pickups.get(s.pickupRequestId) : undefined}
              />
            ))}
          </ul>
        )}
      </section>

      {run.status === 'active' ? (
        <div className="ops-actionbar" data-tour="run-end">
          <Link to={`/run/${run.id}/end`}>
            <Button size="touch" variant="primary" block>
              {progress.left > 0 ? `End the run (${progress.left} stops left)` : 'End the run'}
            </Button>
          </Link>
        </div>
      ) : run.status === 'closed' ? (
        <p className="ops-ok" style={{ margin: 0 }}>
          This run is finished
          {run.closedAt ? (
            <>
              {' '}
              <TimeInState since={run.closedAt} suffix="ago" />
            </>
          ) : null}
          .
        </p>
      ) : null}
    </div>
  );
}

function StopRow({
  stop,
  runId,
  currency,
  isNext,
  shipment,
  pickup,
}: {
  stop: StopView;
  runId: string;
  currency: string;
  isNext: boolean;
  shipment: OpsShipment | undefined;
  pickup: OpsPickupRequest | undefined;
}) {
  const status = stopStatus(stop.status, stop.waiting);
  const address = stop.address ?? shipment?.consignee.address ?? pickup?.address;
  const phones = stop.contact?.phones.length
    ? stop.contact.phones
    : (shipment?.consignee.phones ?? (pickup ? [pickup.contact.phoneE164] : []));
  const geo = stop.geo ?? pickup?.geo;
  const boxCount = stopBarcodes(stop).length;
  const finished = stop.status === 'done' || stop.status === 'failed' || stop.status === 'skipped';
  return (
    <li
      className="ops-row ops-stop"
      data-tone={
        stop.status === 'done'
          ? 'done'
          : stop.status === 'failed'
            ? 'failed'
            : isNext
              ? 'next'
              : undefined
      }
    >
      <div className="ops-row__top">
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="ops-seq" aria-label={`Stop ${stop.sequence + 1}`}>
            {stop.sequence + 1}
          </span>
          <strong>{stop.label}</strong>
        </span>
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>
      <div className="ops-muted">
        {stop.kind === 'pickup'
          ? 'Pickup'
          : `Delivery · ${boxCount} box${boxCount === 1 ? '' : 'es'}`}
        {stop.collectMinor > 0 ? ` · collect ${fmt(stop.collectMinor, currency)}` : ''}
      </div>
      {address ? <div>{formatAddress(address)}</div> : null}
      {!finished ? (
        <>
          <ContactLinks geo={geo} address={address} phones={phones} label={stop.label} />
          <Link to={`/run/${runId}/stop/${stop.id}`}>
            <Button size="touch" variant={isNext ? 'cta' : 'primary'} block>
              {stop.kind === 'pickup' ? 'Open the pickup' : 'Open the delivery'}
            </Button>
          </Link>
        </>
      ) : (
        <Link to={`/run/${runId}/stop/${stop.id}`}>
          <Button size="lg" variant="ghost">
            See what was recorded
          </Button>
        </Link>
      )}
    </li>
  );
}
